import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import TeacherModal from "../../modals/admin/TeacherModal";
import TeacherCards from "../../Components/admin/Cards/TeacherCards";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";

import {
  addTeacher,
  updateTeacher,
  deleteTeacher,
  fetchTeachers,
  setPageNumber,
  setNameFilter,
  setCampusId,
  setClassId,
  setSectionId,
  resetFilters,
} from "../../redux/teachersSlice";
import { fetchCampuses, fetchCampusClassesDetail } from "../../redux/campusesSlice";
import { Users, UserCheck, UserX, UserPlus } from "lucide-react";
import NotificationsModal from "../../Components/Common/NotificationModal";
import { formatDate, uploadImage } from "../../utils";
import {
  sendNotification,
  clearNotificationStatus,
} from "../../redux/notificationsSlice";

import TeacherTable from "./components/TeacherTable";
import TeacherFilters from "./components/TeacherFilters";
import TeacherPagination from "./components/TeacherPagination";

const Teachers = () => {
  const { t } = useTranslation();
  const { register, watch, handleSubmit, reset, setValue } = useForm();
  const dispatch = useDispatch();
  const password = watch("password");
  const teachers = useSelector((state) => state.teachers.teachers);
  const loading = useSelector((state) => state.teachers.loading);
  const error = useSelector((state) => state.teachers.error);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTeacherId, setCurrentTeacherId] = useState(null);
  const [currentPicture, setCurrentPicture] = useState(null);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadType, setUploadType] = useState("");
  const [isNotificationsModal, setIsNotificationsModal] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);

  // Redux state
  const totalCount = useSelector((state) => state.teachers.totalCount);
  const pageSize = useSelector((state) => state.teachers.pageSize);
  const pageNumber = useSelector((state) => state.teachers.pageNumber);
  const nameFilterValue = useSelector((state) => state.teachers.nameFilter);
  const campusIdValue = useSelector((state) => state.teachers.campusId);
  const classIdValue = useSelector((state) => state.teachers.classId);
  const sectionIdValue = useSelector((state) => state.teachers.sectionId);

  const { campuses, campusClassesDetail } = useSelector((state) => state?.campuses || []);
  const classes = useSelector((state) => state.classes.classes || []);

  const notificationStatus = useSelector(
    (state) => state.notifications.sendStatus
  );
  const notificationError = useSelector((state) => state.notifications.error);

  // Initial data loading
  useEffect(() => {
    dispatch(
      fetchTeachers({
        pageNumber,
        pageSize,
        nameFilter: nameFilterValue,
        campusId: campusIdValue,
        classId: classIdValue,
        sectionId: sectionIdValue,
      })
    );
    dispatch(fetchCampuses());
    dispatch(fetchCampusClassesDetail());
  }, [
    dispatch,
    pageNumber,
    pageSize,
    nameFilterValue,
    campusIdValue,
    classIdValue,
    sectionIdValue,
  ]);

  useEffect(() => {
    const handler = debounce(() => {
      dispatch(setNameFilter(searchQuery));
    }, 500);
    
    handler();
    
    return () => {
      handler.cancel();
    };
  }, [searchQuery, dispatch]);

  useEffect(() => {
    if (notificationStatus === "succeeded") {
      toast.success(t("NotificationSentSuccessfully"));
      dispatch(clearNotificationStatus());
    } else if (notificationStatus === "failed" && notificationError) {
      toast.error(`${t("FailedToSendNotification")}: ${notificationError}`);
      dispatch(clearNotificationStatus());
    }
  }, [notificationStatus, notificationError, dispatch, t]);

  const teacherCardData = [
    {
      title: t("TotalTeachers"),
      icon: Users,
      number: totalCount || 0,
      userStatus: "",
    },
    {
      title: t("Active"),
      icon: UserCheck,
      number:
        teachers?.filter((t) => t?.teacher?.userStatus === "Active").length ||
        0,
      userStatus: "Active",
    },
    {
      title: t("Resigned"),
      icon: UserX,
      number:
        teachers?.filter((t) => t?.teacher?.userStatus === "Resigned").length ||
        0,
      userStatus: "Resigned",
    },
    {
      title: t("UnpaidLeaves"),
      icon: UserX,
      number:
        teachers?.filter((t) => t?.teacher?.userStatus === "Unpaid Leaves")
          .length || 0,
      userStatus: "Unpaid Leaves",
    },
    {
      title: t("Promoted"),
      icon: UserPlus,
      number:
        teachers?.filter((t) => t?.teacher?.userStatus === "Promoted").length ||
        0,
      userStatus: "Promoted",
    },
  ];

  const onSubmit = async (data, selectedFile) => {
    let fileUrl = null;
    if (selectedFile) {
      fileUrl = await uploadImage(selectedFile);
    } else if (isEdit) {
      fileUrl = currentPicture;
    }

    const formatTime = (time) => {
      if (!time) return null;
      // If time is already in HH:mm:ss format, return it
      if (time.includes(':')) {
        const parts = time.split(':');
        // Ensure it has seconds
        return parts.length === 3 ? time : `${time}:00`;
      }
      return null;
    };

    const payload = {
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      cnic: data.cnic,
      location: data.location,
      dob: data.dob,
      gender: data.gender,
      joiningDate: data.joiningDate,
      userStatus: data.userStatus,
      maritalStatus: data.maritalStatus,
      campusId: data.campusId,
      profilePicture: fileUrl || null,
      professionalExperience: {
        organizationName: data.organizationName || "",
        designation: data.designation || "",
        startedOn: data.startedOn || null,
        endedOn: data.endedOn || null,
        lastSalaryDrawn: data.lastSalaryDrawn || 0,
        mainResponsibilities: data.mainResponsibilities || "",
        other: data.other || "",
        academicInstitute: data.academicInstitute || "",
        completedYear: data.completedYear || 0,
        majorSubject: data.majorSubject || "",
        cgpa: data.cgpa || 0,
        percentage: data.percentage || 0,
      },
      academicPerformance: {
        courseLevel: data.courseLevel || "",
        firstSemResult: data.firstSemResult || 0,
        secondSemResult: data.secondSemResult || 0,
        thirdSemResult: data.thirdSemResult || 0,
        overallAnnualResult: data.overallAnnualResult || 0,
      },
      availability: {
        maxHoursPerWeek: parseInt(data.maxHoursPerWeek, 10),
        fromTime: formatTime(data.fromTime), // Format to HH:mm:ss
        toTime: formatTime(data.toTime), // Format to HH:mm:ss
        days: data.selectedDays || [],
      },

      classAssignment: (data.addedClasses || []).map((classData) => ({
        classId: classData.classId,
        classDetails: (classData.classDetails || []).map((detail) => ({
          sectionId: detail.sectionId,
          subjectId: detail.subjectId,
        })),
      })),
    };

    try {
      if (isEdit) {
        await dispatch(
          updateTeacher({ id: currentTeacherId, updatedTeacher: payload })
        );
        toast.success(t("TeacherUpdatedSuccessfully"));
      } else {
        await dispatch(addTeacher(payload));
        toast.success(t("TeacherAddedSuccessfully"));
      }
    } catch (err) {
      // Handle error here
      if (err.response?.data?.message === "Teacher already registered") {
        toast.error(t("TeacherAlreadyRegistered"));
      } else {
        toast.error(`${t("FailedToAddUpdateTeacher")}: ${err.message}`);
      }
    }

    reset();
    setIsModalOpen(false);
    handleModalClose();
  };

const handleEdit = (id) => {
  const teacher = teachers.find((teacher) => teacher.teacher?.id === id);
  // Set form values
  setValue("firstName", teacher.teacher?.firstName);
  setValue("lastName", teacher.teacher?.lastName);
  setValue("email", teacher.teacher?.email);
  setValue("phone", teacher.teacher?.phone);
  setValue("location", teacher.teacher?.location);
  setValue("dob", formatDate(teacher.teacher?.dob));
  setValue("gender", teacher.teacher?.gender);
  setValue("password", "");
  setValue("joiningDate", formatDate(teacher.teacher?.joiningDate));
  setValue("cnic", teacher.teacher?.cnic);
  setValue("userStatus", teacher.teacher?.userStatus);
  setValue("employeeCode", teacher.teacher?.employeeCode);
  setValue("maritalStatus", teacher.teacher?.maritalStatus);
  setValue("campusId", teacher.teacher?.campusId); // Ensure campusId is set
  // Academic and professional details
  setValue("academicInstitute", teacher.professionalExperience?.academicInstitute);
  setValue("completedYear", teacher.professionalExperience?.completedYear);
  setValue("majorSubject", teacher.professionalExperience?.majorSubject);
  setValue("cgpa", teacher.professionalExperience?.cgpa);
  setValue("percentage", teacher.professionalExperience?.percentage);
  setValue("organizationName", teacher.professionalExperience?.organizationName);
  setValue("designation", teacher.professionalExperience?.designation);
  setValue("startedOn", formatDate(teacher.professionalExperience?.startedOn));
  setValue("endedOn", formatDate(teacher.professionalExperience?.endedOn));
  setValue("lastSalaryDrawn", teacher.professionalExperience?.lastSalaryDrawn);
  setValue("mainResponsibilities", teacher.professionalExperience?.mainResponsibilities);
  setValue("other", teacher.professionalExperience?.other);
  setValue("maxHoursPerWeek", teacher.availability?.maxHoursPerWeek?.toString());
  setValue("fromTime", teacher.availability?.fromTime);
  setValue("toTime", teacher.availability?.toTime);
  setSelectedDays(teacher.availability?.days || []);
  setSelectedClasses(teacher.assignedClasses || []); // Ensure assignedClasses is set
  setCurrentTeacherId(id);
  setCurrentPicture(teacher.teacher?.profilePicture);
  setIsEdit(true);
  // Delay opening modal to ensure form values are set
  setTimeout(() => {
    setIsModalOpen(true);
  }, 0);
};
  const handleBulkUpload = (file) => {
    const formData = new FormData();
    formData.append("file", file);

    // Dispatch action to upload the bulk file
    // dispatch(addParentBulk(formData))
    //   .then(() => toast.success("Bulk upload successful!"))
    //   .catch(() => toast.error("Bulk upload failed!"));
  };

  const handleModalClose = () => {
    setUploadType(""); // Reset the dropdown value
    setIsEdit(false);
  };

  const handleUploadTypeChange = (type) => {
    setUploadType(type);
    setIsModalOpen(true);
  };

  const toggleView = (index) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  const handleDelete = (id) => {
    dispatch(deleteTeacher(id))
      .unwrap()
      .then(() => toast.success(t("TeacherDeletedSuccessfully")))
      .catch((err) => toast.error(`${t("FailedToDeleteTeacher")}: ${err}`));
  };

  const handleCardClick = (card, index) => {
    setStatusFilter(card.userStatus);
    dispatch(
      fetchTeachers({
        pageNumber: 1,
        pageSize,
        nameFilter: nameFilterValue,
        campusId: campusIdValue,
        classId: classIdValue,
        sectionId: sectionIdValue,
      })
    );
    setSelectedCard(index);
  };

  const handlePageChange = (newPage) => {
    if (newPage !== pageNumber) {
      dispatch(setPageNumber(newPage));
    }
  };

  const uniqueLocation = Array.from(
    new Set(teachers?.map((teacher) => teacher?.teacher?.location))
  ).filter(Boolean);

  const uniqueSubjects = Array.from(
    new Set(
      campusClassesDetail?.flatMap((campus) =>
        campus.classes?.flatMap((cls) =>
          cls.sections?.flatMap((section) => section.subjects?.map((subject) => subject.subjectName))
        )
      )
    )
  ).filter(Boolean);

  // Get unique campus, class, and section IDs/names for filters
  const uniqueCampuses =
    campuses?.map((campus) => ({
      id: campus.id,
      name: campus.name,
    })) || [];

  const uniqueClasses =
    campusClassesDetail?.flatMap((campus) =>
      campus.classes?.map((cls) => ({
        id: cls.id,
        name: cls.className,
        campusId: campus.id,
      }))
    ) || [];

  const uniqueSections =
    campusClassesDetail?.flatMap((campus) =>
      campus.classes?.flatMap((cls) =>
        cls.sections?.map((section) => ({
          id: section.id,
          name: section.sectionName,
          classId: cls.id,
        }))
      )
    ) || [];

  // Filter classes based on selected campus
  const filteredClasses = campusIdValue
    ? uniqueClasses.filter((cls) => cls.campusId === campusIdValue)
    : uniqueClasses;

  // Filter sections based on selected class
  const filteredSections = classIdValue
    ? uniqueSections.filter((section) => section.classId === classIdValue)
    : uniqueSections;

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  // Handle campus filter change
  const handleCampusChange = (value) => {
    dispatch(setCampusId(value));
    // Reset dependent filters when parent filter changes
    dispatch(setClassId(""));
    dispatch(setSectionId(""));
  };

  // Handle class filter change
  const handleClassChange = (value) => {
    dispatch(setClassId(value));
    // Reset section when class changes
    dispatch(setSectionId(""));
  };

  // Handle section filter change
  const handleSectionChange = (value) => {
    dispatch(setSectionId(value));
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setSelectedCard(null);
    dispatch(resetFilters());
  };
  const filteredTeachers = teachers || [];

  const teachersToDisplay = filteredTeachers.filter((teacher) => {
    const matchesStatus = statusFilter
      ? teacher?.teacher?.userStatus === statusFilter
      : true;
    return matchesStatus;
  });

  const totalPages = Math.ceil(totalCount / pageSize);
  const currentTeachers = teachersToDisplay;

  const toggleTeacherSelection = (teacherId, teacherName) => {
    setSelectedTeachers((prev) => {
      const isSelected = prev.some((teacher) => teacher.id === teacherId);
      if (isSelected) {
        return prev.filter((teacher) => teacher.id !== teacherId);
      } else {
        return [...prev, { id: teacherId, name: teacherName }];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedTeachers.length === teachers.length) {
      setSelectedTeachers([]); // Deselect all
    } else {
      setSelectedTeachers(
        currentTeachers.map((teacher) => ({
          id: teacher.teacher?.id,
          name: `${teacher.teacher?.firstName} ${teacher.teacher?.lastName}`,
        }))
      );
    }
  };

  const onSendNotification = async (payload) => {
    // Process HTML in the message (to handle links)
    const requestData = {
      UserIds: payload.recipients.map((recipient) => recipient.id),
      Title: payload.title,
      Body: payload.message, // The message may now contain HTML tags for links
    };

    // Dispatch the async action for sending notification
    dispatch(sendNotification(requestData));
  };

  const handleSubmitNotification = (payload) => {
    onSendNotification(payload);
    setIsModalOpen(false);
    setSelectedTeachers([]);
  };

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      {/* Grid for Teacher Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        {teacherCardData.map((card, index) => (
          <div
            key={index}
            onClick={() => handleCardClick(card, index)}
            className="cursor-pointer"
          >
            <TeacherCards
              icon={card.icon}
              title={card.title}
              number={card.number}
              isSelected={selectedCard === index}
              className="truncate cursor-pointer"
            />
          </div>
        ))}
      </div>

      <TeacherFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        campusIdValue={campusIdValue}
        classIdValue={classIdValue}
        sectionIdValue={sectionIdValue}
        campuses={campuses}
        campusClassesDetail={campusClassesDetail}
        uploadType={uploadType}
        handleUploadTypeChange={handleUploadTypeChange}
        setIsNotificationsModal={setIsNotificationsModal}
        dispatch={dispatch}
        setCampusId={setCampusId}
        setClassId={setClassId}
        setSectionId={setSectionId}
        resetFilters={resetFilters}
      />

      <TeacherModal
        isModalOpen={isModalOpen}
        isEdit={isEdit}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        setIsModalOpen={setIsModalOpen}
        register={register}
        password={password}
        reset={reset}
        currentPicture={currentPicture}
        campusClasses={campusClassesDetail}
        selectedClasses={selectedClasses}
        handleBulkUpload={handleBulkUpload}
        uploadType={uploadType}
        onModalClose={handleModalClose}
        campuses={campuses}
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        watch={watch}
      />

      {isNotificationsModal && (
        <NotificationsModal
          selectedItems={selectedTeachers}
          toggleItemSelection={toggleTeacherSelection}
          handleSubmitNotification={handleSubmitNotification}
          onClose={() => setIsNotificationsModal(false)}
        />
      )}

      <TeacherTable
        loading={loading}
        filteredTeachers={filteredTeachers}
        expandedRowIndex={expandedRowIndex}
        selectedTeachers={selectedTeachers}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        toggleView={toggleView}
        toggleTeacherSelection={toggleTeacherSelection}
        toggleSelectAll={toggleSelectAll}
      />

      <TeacherPagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        handlePageChange={(page) => handlePageChange(page)}
      />

      <Toaster />
    </div>
  );
};

export default Teachers;